import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, X, Loader2, Mail, ChevronLeft, Sparkles, Crown } from "lucide-react";
import { toast } from "sonner";

import api, { formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

function PlanCard({ plan, current, billingCycle, onCta }) {
  const isCurrent = plan.key === current;
  const isWaitlist = plan.cta === "Notify Me When Available";
  const isHighlight = plan.highlight;

  // Free + Enterprise are static; Starter + Growth swap on cycle.
  const isStatic = plan.key === "free" || plan.key === "enterprise";
  const displayPrice = isStatic
    ? plan.price
    : (billingCycle === "yearly" ? plan.yearly_price : plan.monthly_price) || plan.price;
  const displayPeriod = isStatic
    ? plan.period
    : (billingCycle === "yearly" ? "/yr" : "/mo");
  const showSavings = !isStatic && billingCycle === "yearly" && plan.yearly_savings;

  return (
    <div
      className={`relative flex flex-col p-6 rounded-md border bg-white transition-shadow hover:shadow-md ${
        isHighlight ? "border-emerald-500 ring-1 ring-emerald-500" : "border-slate-200"
      }`}
      data-testid={`plan-card-${plan.key}`}
    >
      {isHighlight && (
        <div className="absolute -top-3 left-6 bg-emerald-500 text-slate-900 text-[10px] font-semibold tracking-widest uppercase px-2.5 py-1 rounded-sm flex items-center gap-1">
          <Sparkles className="w-3 h-3" /> Most popular
        </div>
      )}
      {isCurrent && (
        <div className="absolute -top-3 right-6 bg-slate-900 text-white text-[10px] font-semibold tracking-widest uppercase px-2.5 py-1 rounded-sm">
          Your plan
        </div>
      )}

      <div>
        <h3 className="font-heading text-2xl font-semibold text-slate-900">{plan.name}</h3>
        <p className="text-sm text-slate-500 mt-1 min-h-[40px]">{plan.tagline}</p>
      </div>

      <div className="mt-4 flex items-baseline gap-1" data-testid={`plan-price-${plan.key}`}>
        <span className="font-heading text-4xl font-bold text-slate-900">
          {displayPrice}
        </span>
        {displayPeriod && <span className="text-sm text-slate-500">{displayPeriod}</span>}
      </div>
      {showSavings ? (
        <p className="text-xs font-medium text-emerald-700 mt-1" data-testid={`plan-savings-${plan.key}`}>
          {plan.yearly_savings}
        </p>
      ) : (
        <p className="text-xs text-transparent mt-1 select-none">.</p>
      )}

      <ul className="mt-5 space-y-2.5 flex-1" data-testid={`plan-features-${plan.key}`}>
        {plan.features.map((f, i) => (
          <li key={i} className="flex items-start gap-2 text-sm">
            {f.included ? (
              <Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
            ) : (
              <X className="w-4 h-4 text-slate-300 mt-0.5 shrink-0" />
            )}
            <span className={f.included ? "text-slate-700" : "text-slate-400 line-through"}>{f.label}</span>
          </li>
        ))}
      </ul>

      <Button
        type="button"
        disabled={isCurrent}
        onClick={() => onCta(plan)}
        className={`mt-6 w-full font-medium ${
          isCurrent
            ? "bg-slate-100 text-slate-500 hover:bg-slate-100 cursor-default"
            : isHighlight
              ? "bg-emerald-500 hover:bg-emerald-400 text-slate-900"
              : isWaitlist
                ? "bg-slate-900 hover:bg-slate-800 text-white"
                : "bg-slate-900 hover:bg-slate-800 text-white"
        }`}
        data-testid={`plan-cta-${plan.key}`}
      >
        {isCurrent ? "Current plan" : plan.cta}
      </Button>
    </div>
  );
}

function WaitlistDialog({ tier, defaultEmail, onClose, onDone }) {
  const [email, setEmail] = useState(defaultEmail || "");
  const [submitting, setSubmitting] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) {
      toast.error("Please enter a valid email");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/billing/waitlist", { tier: tier.key, email });
      if (data?.email_sent) {
        toast.success(`We've got you on the ${tier.name} list — we'll be in touch.`);
      } else {
        toast.success(`You're on the ${tier.name} list. We'll reach out shortly.`);
      }
      onDone?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't save you to the list");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/40 backdrop-blur-sm p-4" onClick={onClose}>
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-white border border-slate-200 rounded-md max-w-md w-full p-6 shadow-xl"
        data-testid="waitlist-dialog"
      >
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-sm bg-emerald-500 grid place-items-center">
            <Mail className="w-4 h-4 text-slate-900" />
          </div>
          <div>
            <p className="label-overline">Notify me</p>
            <h3 className="font-heading text-xl font-semibold mt-0.5">{tier.name} — coming soon</h3>
          </div>
        </div>
        <p className="text-sm text-slate-500 mt-4">
          We&apos;ll send a single email to this address as soon as the {tier.name} plan is live. No spam, no marketing.
        </p>
        <div className="mt-4">
          <Label htmlFor="waitlist-email" className="label-overline">Email</Label>
          <Input
            id="waitlist-email"
            type="email"
            placeholder="john.doe@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="mt-2"
            data-testid="waitlist-email-input"
            autoFocus
          />
        </div>
        <div className="mt-6 flex items-center justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose} data-testid="waitlist-cancel">Cancel</Button>
          <Button type="submit" disabled={submitting} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="waitlist-submit">
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
            Notify me
          </Button>
        </div>
      </form>
    </div>
  );
}

export default function Pricing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [current, setCurrent] = useState("free");
  const [loading, setLoading] = useState(true);
  const [waitlistTier, setWaitlistTier] = useState(null);
  const [billingCycle, setBillingCycle] = useState("monthly");

  useEffect(() => {
    api.get("/billing/plans").then(({ data }) => {
      setPlans(data.plans || []);
      setCurrent(data.current_plan || "free");
    }).catch(() => {
      toast.error("Couldn't load pricing");
    }).finally(() => setLoading(false));
  }, []);

  const onCta = (plan) => {
    if (plan.key === current) return;
    if (plan.cta === "Notify Me When Available") {
      setWaitlistTier(plan);
      return;
    }
    if (plan.cta === "Talk to Sales") {
      window.location.href = `mailto:uche@kuvora.com?subject=Array%20Engine%20Enterprise%20enquiry`;
      return;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="pricing-brand-link">
            <img src="/array-logo.png" alt="Array Engine" className="h-7 w-auto" />
          </Link>
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="pricing-back">
            <ChevronLeft className="w-4 h-4 mr-1" /> Back
          </Button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-14">
        <div className="text-center max-w-2xl mx-auto">
          <p className="label-overline flex items-center justify-center gap-2">
            <Crown className="w-3.5 h-3.5 text-emerald-600" /> Pricing
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tight font-semibold text-slate-900 mt-3">
            Plans that scale with your impact
          </h1>
          <p className="text-slate-600 mt-4 text-lg leading-relaxed">
            Start free. Upgrade when you&apos;re ready to ditch the watermark and unlock the full reporting stack.
          </p>

          <div
            className="mt-7 inline-flex items-center gap-1 p-1 bg-white border border-slate-200 rounded-full"
            role="tablist"
            aria-label="Billing cycle"
            data-testid="billing-cycle-toggle"
          >
            <button
              type="button"
              role="tab"
              aria-selected={billingCycle === "monthly"}
              onClick={() => setBillingCycle("monthly")}
              className={`px-4 py-1.5 text-sm font-medium rounded-full transition-colors ${
                billingCycle === "monthly"
                  ? "bg-slate-900 text-white"
                  : "text-slate-600 hover:text-slate-900"
              }`}
              data-testid="billing-cycle-monthly"
            >
              Monthly
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={billingCycle === "yearly"}
              onClick={() => setBillingCycle("yearly")}
              className={`px-4 py-1.5 text-sm font-medium rounded-full transition-colors flex items-center gap-1.5 ${
                billingCycle === "yearly"
                  ? "bg-slate-900 text-white"
                  : "text-slate-600 hover:text-slate-900"
              }`}
              data-testid="billing-cycle-yearly"
            >
              Yearly
              <span
                className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-sm ${
                  billingCycle === "yearly"
                    ? "bg-emerald-500 text-slate-900"
                    : "bg-emerald-100 text-emerald-700"
                }`}
              >
                Save ~17%
              </span>
            </button>
          </div>
        </div>

        {loading ? (
          <div className="grid place-items-center mt-16 text-slate-500"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-12" data-testid="pricing-grid">
            {plans.map((p) => (
              <PlanCard key={p.key} plan={p} current={current} billingCycle={billingCycle} onCta={onCta} />
            ))}
          </div>
        )}

        <div className="mt-14 max-w-3xl mx-auto bg-white border border-slate-200 rounded-md p-6 text-center" data-testid="pricing-watermark-note">
          <p className="text-sm text-slate-500">
            <strong className="text-slate-900">Free tier reports</strong> include an &quot;Array Engine&quot; attribution at the foot
            of every PDF and web view. Upgrade to Starter or higher to publish with your own organization branding.
          </p>
        </div>

        <p className="text-center text-xs text-slate-400 mt-10">
          Questions? Email <a href="mailto:uche@kuvora.com" className="underline">uche@kuvora.com</a>.
        </p>
      </div>

      {waitlistTier && (
        <WaitlistDialog
          tier={waitlistTier}
          defaultEmail={user?.email || ""}
          onClose={() => setWaitlistTier(null)}
          onDone={() => setWaitlistTier(null)}
        />
      )}
    </div>
  );
}
